/**
 * Central billedlogik for biler.
 *
 * Fallback-kæde: variant-billede → model-billede → null (kaldere viser placeholder).
 * Supabase-storage-URL'er omskrives til billed-transformationsendepunktet,
 * så bilkort ikke henter fuldopløsning på mobil.
 */

const STORAGE_PUBLIC = '/storage/v1/object/public/';
const STORAGE_RENDER = '/storage/v1/render/image/public/';

export type CarImageSource = {
  variant?: { image_url?: string | null } | null;
  model?: { image_url?: string | null } | null;
};

/** Standardbredder til srcset — dækker mobil, tablet og desktop-kort. */
export const CAR_IMAGE_WIDTHS = [320, 480, 640, 960] as const;

function isTransformable(url: string): boolean {
  return url.includes(STORAGE_PUBLIC);
}

/** Tilføjer bredde/kvalitet til Supabase-billeder; øvrige URL'er returneres uændret. */
export function resizeCarImage(url: string, width: number): string {
  if (!isTransformable(url)) return url;
  const base = url.replace(STORAGE_PUBLIC, STORAGE_RENDER);
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}width=${width}&resize=contain&quality=75`;
}

/** Vælger det bedste tilgængelige billede for en bil. */
export function pickCarImage(car: CarImageSource): string | null {
  const variantImage = car.variant?.image_url?.trim();
  if (variantImage) return variantImage;
  const modelImage = car.model?.image_url?.trim();
  if (modelImage) return modelImage;
  return null;
}

/** Færdig `src` i ønsket bredde, eller null hvis der ikke findes et billede. */
export function buildCarImageUrl(car: CarImageSource, width = 640): string | null {
  const url = pickCarImage(car);
  return url ? resizeCarImage(url, width) : null;
}

/** srcset-streng til responsive bilbilleder. */
export function buildCarImageSrcSet(car: CarImageSource): string | undefined {
  const url = pickCarImage(car);
  if (!url || !isTransformable(url)) return undefined;
  return CAR_IMAGE_WIDTHS.map((w) => `${resizeCarImage(url, w)} ${w}w`).join(', ');
}

/** Standard `sizes` for bilkort i grid. */
export const CAR_CARD_SIZES = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 320px';
